import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { AlertTriangle, Wifi, WifiOff, Cpu, CheckCircle2, XCircle, Eye } from 'lucide-react';
import { getAlertas, resolverAlerta, ignorarAlerta } from '../../lib/gestion4satApi';
import { toast } from 'sonner';

export default function G4sAlertasPage() {
  const [alertas, setAlertas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolucion, setResolucion] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await getAlertas({ estado: filtroEstado === 'todas' ? '' : filtroEstado, tipo: filtroTipo === 'all' ? '' : filtroTipo, limit: 100 });
      setAlertas(d.alertas);
      setTotal(d.total);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [filtroEstado, filtroTipo]);

  useEffect(() => { load(); }, [load]);

  const handleResolver = async () => {
    try {
      await resolverAlerta(resolveDialog.id, { resolucion });
      toast.success('Alerta resuelta');
      setResolveDialog(null);
      setResolucion('');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleIgnorar = async (id) => {
    try {
      await ignorarAlerta(id);
      toast.success('Alerta ignorada');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const tipoIcons = {
    sim_fantasma: { icon: Wifi, color: 'text-red-500', bg: 'bg-red-500/10' },
    perdida_transmision: { icon: WifiOff, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  };

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-alertas-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Alertas</h1>
          <p className="text-sm text-muted-foreground">{total} alertas {filtroEstado === 'pendiente' ? 'pendientes' : ''}</p>
        </div>

        <div className="flex gap-3">
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pendiente">Pendientes</SelectItem>
              <SelectItem value="resuelta">Resueltas</SelectItem>
              <SelectItem value="ignorada">Ignoradas</SelectItem>
              <SelectItem value="todas">Todas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="sim_fantasma">SIM Fantasma</SelectItem>
              <SelectItem value="perdida_transmision">Pérdida Transmisión</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)
          ) : alertas.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="h-10 w-10 text-green-500 mb-3" />
                <p className="text-muted-foreground">Sin alertas {filtroEstado === 'pendiente' ? 'pendientes' : ''}</p>
              </CardContent>
            </Card>
          ) : (
            alertas.map(alerta => {
              const tipo = tipoIcons[alerta.tipo] || { icon: AlertTriangle, color: 'text-yellow-500', bg: 'bg-yellow-500/10' };
              const Icon = tipo.icon;
              return (
                <Card key={alerta.id} className="hover:border-border/80">
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg ${tipo.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`h-5 w-5 ${tipo.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs capitalize">{alerta.tipo?.replace('_', ' ')}</Badge>
                        <Badge className={
                          alerta.estado === 'pendiente' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                          alerta.estado === 'resuelta' ? 'bg-green-500/10 text-green-600 border-green-500/20' :
                          'bg-gray-500/10 text-gray-600 border-gray-500/20'
                        }>{alerta.estado}</Badge>
                      </div>
                      <p className="text-sm">{alerta.descripcion}</p>
                      {alerta.imei && <p className="text-xs font-mono text-muted-foreground mt-1">IMEI: {alerta.imei} {alerta.iccid && `| ICCID: ${alerta.iccid}`}</p>}
                      {alerta.accion_sugerida && <p className="text-xs text-blue-500 mt-1">{alerta.accion_sugerida}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{new Date(alerta.created_at).toLocaleString('es-CL')}</p>
                    </div>
                    {alerta.estado === 'pendiente' && (
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => setResolveDialog(alerta)} data-testid={`resolver-${alerta.id}`}>
                          Resolver
                        </Button>
                        <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => handleIgnorar(alerta.id)}>
                          Ignorar
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        <Dialog open={!!resolveDialog} onOpenChange={() => setResolveDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resolver Alerta</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm">{resolveDialog?.descripcion}</p>
              <Textarea
                placeholder="Descripción de la resolución..."
                value={resolucion}
                onChange={e => setResolucion(e.target.value)}
                data-testid="input-resolucion"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResolveDialog(null)}>Cancelar</Button>
              <Button onClick={handleResolver} className="bg-green-600 hover:bg-green-700" data-testid="btn-confirmar-resolver">Resolver</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </G4sLayout>
  );
}
